import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth, User } from '../context/AuthContext';
import { useDisposableEmailNotice } from '../hooks/useDisposableEmailNotice';
import { useSkillStoreVisibility } from '../hooks/useSkillStoreVisibility';
import { isIOSApp } from '../utils/platformDetection';

import { AuthButtons } from './AuthButtons';
import { BrandPendulumMark } from './BrandPendulumMark';
// import { HeaderGitHubConnect } from './HeaderGitHubConnect';
import { UpgradeButton } from './CreditDisplay';
import { DisposableEmailNoticeModal } from './DisposableEmailNoticeModal';
import { DownloadAppsControl } from './DownloadAppsModal';
import { ImpersonationUserMenu } from './ImpersonationUserMenu';
import { NavbarTabs } from './NavbarTabs';
import { ReferralModal } from './ReferralModal';
import { SupportNotificationBell } from './Support/SupportNotificationBell';
import { UserMenu } from './UserMenu';

interface HeaderProps {
  onLoginClick: () => void;
  onSignUpClick: () => void;
  authIsLoading?: boolean;
  centerContent?: React.ReactNode;
}

// The explicit SVG attributes keep the icon driven by the button's current color.
// eslint-disable-next-line max-lines-per-function
const GiftButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    aria-label="Get free credits"
    className="btn-gift-nav"
    onClick={onClick}
    title="Get free credits"
    type="button"
  >
    <svg
      aria-hidden="true"
      className="gift-icon-svg"
      fill="none"
      height="22"
      viewBox="0 0 24 24"
      width="22"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        fill="currentColor"
        fillOpacity="0.08"
        height="11"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        width="18"
        x="3"
        y="10"
      />
      <rect
        fill="currentColor"
        fillOpacity="0.12"
        height="5"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
        width="14"
        x="5"
        y="6"
      />
      <line
        stroke="currentColor"
        strokeWidth="1.8"
        x1="12"
        x2="12"
        y1="6"
        y2="21"
      />
      <path
        d="M12 6C12 6 9 3 7 3C5 3 4 4.5 5 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <path
        d="M12 6C12 6 15 3 17 3C19 3 20 4.5 19 6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  </button>
);

const shouldHideNavbarTabs = (pathname: string) => /^\/projects\/[^/]+\/team(?:\/|$)/.test(pathname)
  || /^\/team(?:\/|$)/.test(pathname);

interface UserAreaProps {
  user: User | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  onLogin: () => void;
  onSignUp: () => void;
}

const UserArea: React.FC<UserAreaProps> = (props) => {
  if (!props.isAuthenticated || !props.user) {
    return <AuthButtons onLogin={props.onLogin} onSignUp={props.onSignUp} />;
  }
  if (props.isImpersonating) return <ImpersonationUserMenu />;

  return <UserMenu user={props.user} />;
};

const ChatLink: React.FC = () => (
  <>
    <Link className="header-nav-link" to="/chat">Chat</Link>
    <Link className="header-nav-link" to="/models">Models</Link>
  </>
);

const ExploreLink: React.FC = () => (!isIOSApp()
  ? <Link className="header-nav-link" to="/explore">Explore</Link>
  : null);

const PublicHeaderLinks: React.FC<{
  isProjectPage: boolean;
  skillStoreVisible: boolean;
}> = ({ isProjectPage, skillStoreVisible }) => (
  <nav className="header-nav">
    <ExploreLink />
    {skillStoreVisible && <Link className="header-nav-link" to="/skill-store">Skill Store</Link>}
    {!isProjectPage && <Link className="header-nav-link" to="/models">Models</Link>}
    {!isProjectPage && <Link className="header-nav-link" to="/pricing">Pricing</Link>}
    {!isProjectPage && <Link className="header-nav-link" to="/about">About</Link>}
  </nav>
);

interface HeaderBarProps extends HeaderProps {
  pathname: string;
  user: User | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  hideUserControls: boolean;
  isProjectPage: boolean;
  skillStoreVisible: boolean;
  onGiftClick: () => void;
}

const HEADER_CENTER_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'auto',
};

const HeaderCenter: React.FC<{
  pathname: string;
  centerContent?: React.ReactNode;
}> = ({ pathname, centerContent }) => {
  if (centerContent) {
    return (
      <div className="header-center-custom" style={HEADER_CENTER_STYLE}>
        {centerContent}
      </div>
    );
  }

  return pathname === '/support/dashboard'
    ? <div className="header-center-title">Support Dashboard</div>
    : null;
};

const HeaderBar: React.FC<HeaderBarProps> = (props) => (
  <header className="header">
    <div className="header-left">
      <Link className="brand-text brand-button" to="/">
        <BrandPendulumMark className="brand-mark" />
        <span className="brand-button__label">8080.AI</span>
      </Link>
    </div>
    {!shouldHideNavbarTabs(props.pathname) && !props.centerContent && <NavbarTabs />}
    <HeaderCenter pathname={props.pathname} centerContent={props.centerContent} />
    <div
      className={`header-right${props.isImpersonating && !props.hideUserControls ? ' header-right--impersonating' : ''}`}
      style={props.authIsLoading
        ? { opacity: 0, pointerEvents: 'none' }
        : { opacity: 1, transition: 'opacity 0.15s ease' }}
    >
      {!props.isAuthenticated && (
        <PublicHeaderLinks
          isProjectPage={props.isProjectPage}
          skillStoreVisible={props.skillStoreVisible}
        />
      )}
      {props.isAuthenticated && props.user && !props.hideUserControls && !props.isProjectPage && <ChatLink />}
      {props.isAuthenticated && props.user && !props.hideUserControls && <ExploreLink />}
      {props.isAuthenticated && !props.isProjectPage && !props.hideUserControls && props.skillStoreVisible && (
        <nav className="header-nav" aria-label="Marketplace">
          <Link className="header-nav-link" to="/skill-store">Skill Store</Link>
        </nav>
      )}
      {!props.hideUserControls && <DownloadAppsControl />}
      {props.isAuthenticated && props.user && !props.hideUserControls && <GiftButton onClick={props.onGiftClick} />}
      {props.isAuthenticated && props.user && !props.hideUserControls && <SupportNotificationBell />}
      {props.isAuthenticated && props.user && !props.hideUserControls && <UpgradeButton />}
      {!props.hideUserControls && (
        <UserArea
          user={props.user}
          isAuthenticated={props.isAuthenticated}
          isImpersonating={props.isImpersonating}
          onLogin={props.onLoginClick}
          onSignUp={props.onSignUpClick}
        />
      )}
    </div>
  </header>
);

const routeFlags = (pathname: string) => ({
  isProjectPage: /^\/projects\/[^/]+/.test(pathname),
  hideUserControls: pathname.startsWith('/planning'),
});

export const Header: React.FC<HeaderProps> = ({
  onLoginClick, onSignUpClick, authIsLoading = false, centerContent,
}) => {
  const { isAuthenticated, user, isImpersonating } = useAuth();
  const location = useLocation();
  const [referralOpen, setReferralOpen] = useState(false);
  const disposableNotice = useDisposableEmailNotice();
  const { isProjectPage, hideUserControls } = routeFlags(location.pathname);
  const skillStoreVisible = useSkillStoreVisibility();

  return (
    <>
      <HeaderBar
        pathname={location.pathname}
        user={user}
        isAuthenticated={isAuthenticated}
        isImpersonating={isImpersonating}
        hideUserControls={hideUserControls}
        isProjectPage={isProjectPage}
        skillStoreVisible={skillStoreVisible}
        authIsLoading={authIsLoading}
        onLoginClick={onLoginClick}
        onSignUpClick={onSignUpClick}
        onGiftClick={() => setReferralOpen(true)}
        centerContent={centerContent}
      />
      <ReferralModal isOpen={referralOpen} onClose={() => setReferralOpen(false)} />
      <DisposableEmailNoticeModal isOpen={disposableNotice.isOpen} onClose={disposableNotice.onClose} />
    </>
  );
};
